import Search from "./layout/Search.tsx";
import SearchTitleResults from "./titles/SearchTitleResults.tsx";
import { searchTitlesLicenceLoader } from "./titles/searchTitlesLicense.ts";
import InsetHomeError from "../InsetHomeError.tsx";
import StartScreen from "./start-screen/StartScreen.tsx";
import SearchCountsResults from "./book-content/SearchCountsResults.tsx";
import {
  searchCounts,
  searchCountsWithNextCursor,
} from "./book-content/searchCounts.ts";
import { LoaderFunction, matchPath, redirect } from "react-router-dom";
import { getSearchQuery } from "./query.ts";
import { searchContent } from "./book-content/searchContent.ts";
import { getBasePathWithTrailingSlash } from "../../../common/env.ts";
import { licenseLoader } from "../actions/license/licenseLoader.ts";
import { timeoutLoader } from "./common/timeoutLoader.ts";
import { favoritesByVbid } from "../actions/favorites/favorites.ts";
import { searchTitlesExploreLoader } from "./titles/searchTitlesExplore.ts";
import { searchTitlesStoreLoader } from "./titles/searchTitlesStore.ts";
import { coursePickerLoader } from "./start-screen/course-picker/coursePickerLoader.ts";

// path can't detect query params
const queryRedirector: LoaderFunction = ({ request }) => {
  const url = new URL(request.url);
  const isRootSearchURL = !!matchPath(
    getBasePathWithTrailingSlash() + "search",
    url.pathname
  );
  const hasQuery = !!getSearchQuery(url.searchParams);

  // Redirect e.g. /search/titles with no ?q= back to the default page
  if (!isRootSearchURL && !hasQuery) {
    return redirect("/search");
  }

  // Redirect e.g. /search?q= to a route that actually shows results
  if (isRootSearchURL && hasQuery) {
    return redirect(`/search/titles${url.search}`);
  }

  return null;
};

export const searchRoutes = {
  path: "/search",
  element: <Search />,
  loader: queryRedirector,
  children: [
    {
      path: "/search",
      element: <StartScreen />,
    },
    {
      path: "/search/titles",
      element: <SearchTitleResults />,
    },
    { path: "/search/book-content", element: <SearchCountsResults /> },
    {
      path: "/search/api",
      children: [
        // Rapidfire activate/return actions cause fetcher and revalidation
        // cancellations, so the UI is out of sync with updates until the final
        // revalidation completes. To avoid this, prevent revalidation and
        // manage all activation/return updates in local state only.
        // See ListTitleResultCard.tsx

        // Revalidation after returning/activating assets could also lead to
        // position shifts since activated assets are part of the user's license.
        // Seeing an asset automatically move from the Explore section
        // to the license section could be a weird user experience.
        {
          path: "/search/api/titles/license",
          loader: searchTitlesLicenceLoader,
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/titles/explore",
          loader: searchTitlesExploreLoader,
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/titles/store",
          loader: searchTitlesStoreLoader,
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/counts",
          loader: searchCounts,
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/paginated-counts/:cursor",
          loader: searchCountsWithNextCursor,
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/content/:vbid",
          loader: searchContent,
          // the only thing that should cause a content search revalidation is the search
          // itself, don't reload when borrow status changes etc
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/typeahead",
          loader: licenseLoader,
          shouldRevalidate: () => false,
        },
        // TO-DO: remove this timeout endpoint (added to ensure borrow/return fetcher
        // loading status despite the revalidation prevention via the license endpoint)
        // once we have a better way to show the asset changes after activation/return.
        {
          path: "/search/api/timeout",
          loader: timeoutLoader,
          shouldRevalidate: () => true,
        },
        {
          path: "/search/api/favorites-by-vbid",
          loader: favoritesByVbid,
          shouldRevalidate: () => false,
        },
        {
          path: "/search/api/courses/:term?/:department?/:course?",
          loader: coursePickerLoader,
        },
      ],
    },
  ],
  errorElement: <InsetHomeError />,
};
